import { BlogPostType, FAQsType, HowToSchemaType } from 'src/types';

type DataObject = Record<string, any>;

/**
 * Generate JSON+LD Schema
 * @param {object} data - JSON LD Data
 * @param {string} url - URL of the Page
 * @param {string} lang - Language
 * @param {('WebPage'| 'FAQPage')} type - Schema Type
 */
function generateJSONLDSchema(
  data: DataObject = {},
  url: string,
  type = 'WebPage',
) {
  const JSONSchema = [];

  const pageSchema: DataObject = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    url: 'https://invideo.io',
    name: 'InVideo Online Video Editor',
  };

  if (type === 'WebPage') {
    pageSchema['@type'] = 'WebPage';
    pageSchema.url = url;

    pageSchema.publisher = {
      logo: {
        width: '190 px',
        height: '58 px',
        name: 'InVideo Logo',
        url: 'https://web-assets.invideo.io/common/prod/logos/purple_logo_with_text.svg',
        '@type': 'ImageObject',
      },
      name: 'InVideo',
      url: 'https://invideo.io',
      sameAs: [
        'https://www.facebook.com/invideo.io/',
        'https://twitter.com/@InVideoOfficial',
        'https://www.instagram.com/invideo.io/',
        'https://www.linkedin.com/company/invideoio',
      ],
      '@type': 'Organization',
    };

    if (data?.Description && data?.Title) {
      pageSchema.name = data.Title;
      pageSchema.description = data.Description;
    }
  }
  JSONSchema.push(pageSchema);

  return JSONSchema;
}

/**
 * Generate JSON+LD Article Schema
 * @param {object} data - JSON LD Data
 * @param {string} url - URL of the Page
 * @param {string} lang - Language
 */
function generateJSONLDSchemaArticle(
  data: BlogPostType,
  url: string,
  lang = 'en',
) {
  const JSONSchema = [];

  if (typeof url !== 'string') {
    throw Error(`Type of url is expected 'string' but recieved ${typeof url}`);
  }

  if (typeof data !== 'object') {
    throw Error(
      `Type of data is expected 'object' but recieved ${typeof data}`,
    );
  }

  const articleSchema: DataObject = {
    '@context': 'https://schema.org/',
    '@type': 'BlogPosting',
    headline: data.seo?.Title,
    articleBody: data.seo?.Description,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url,
    },
    url: url,
    ...(data?.featureImage?.url && {
      image: {
        '@type': 'ImageObject',
        url: data?.featureImage?.url,
      },
    }),
    inLanguage: lang,
    author: {
      '@type': 'Person',
      name: data?.author?.name || 'InVideo Team',
    },
    publisher: {
      '@type': 'Organization',
      logo: {
        width: '190 px',
        height: '58 px',
        name: 'InVideo Logo',
        url: 'https://web-assets.invideo.io/common/prod/logos/purple_logo_with_text.svg',
        '@type': 'ImageObject',
      },
      name: 'InVideo',
      url: 'https://invideo.io',
      sameAs: [
        'https://www.facebook.com/invideo.io/',
        'https://twitter.com/@InVideoOfficial',
        'https://www.instagram.com/invideo.io/',
        'https://www.linkedin.com/company/invideoio',
      ],
    },
    datePublished: data.publishedAt,
  };

  if (data?.videoSchema?.length) {
    const embedList: Array<DataObject> = [];
    data?.videoSchema.forEach((d: DataObject) => {
      embedList.push({
        '@type': 'VideoObject',
        ...(d.name && { name: d.name }),
        ...(d.description && { description: d.description }),
        ...(d.thumbnail?.url && { thumbnailUrl: d.thumbnail.url }),
        ...(d.uploadDate && { uploadDate: d.uploadDate }),
        ...(d.embedUrl && { embedUrl: d.embedUrl }),
      });
    });
    articleSchema.video = embedList;
  }
  JSONSchema.push(articleSchema);
  return JSONSchema;
}

function generateJSONLDSchemaHowTo(
  schemaData: HowToSchemaType[] | undefined,
  url: string,
  lang = 'en',
) {
  const JSONSchema: Array<DataObject> = [];

  if (!schemaData || !Array.isArray(schemaData)) {
    console.error(
      `Type of data is expected 'object' but recieved ${typeof schemaData}`,
    );
    return null;
  }
  if (typeof url !== 'string') {
    console.error(
      `Type of url is expected 'string' but recieved ${typeof url}`,
    );
    return null;
  }

  schemaData.forEach(data => {
    const howToSchema: DataObject = {
      '@context': 'https://schema.org/',
      '@type': 'HowTo',
      ...(data.title && { name: data.title }),
      ...(data.description && { description: data.description }),
      ...(data?.image?.url && {
        image: {
          '@type': 'ImageObject',
          url: data.image.url,
        },
      }),
      step: [],
    };

    if (data.step && Array.isArray(data.step)) {
      data.step.forEach(d => {
        const entity = {
          '@type': 'HowToStep',
          ...((d.StepName || d.StepTitle) && {
            name: d.StepName || d.StepTitle,
          }),
          ...(d.StepPosition && { name: d.StepPosition }),
          ...(d.StepUrl && { url: d.StepUrl }),
          ...(d.StepImage?.url && {
            image: {
              '@type': 'ImageObject',
              url: d.StepImage.url,
            },
          }),
          ...(d.StepDescription && {
            itemListElement: {
              '@type': 'HowToDirection',
              text: d.StepDescription,
            },
          }),
        };
        howToSchema.step.push(entity);
      });
    }

    JSONSchema.push(howToSchema);
  });

  return JSONSchema;
}

/**
 * Generate FAQ Schema
 * @param {object} data - FAQ data
 * @returns {object} JSON+LD schema
 */
function generateJSONLDSchemaFAQ(data?: FAQsType['faq'] | null) {
  const JSONSchema: DataObject[] = [];
  const faqSchema: DataObject = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
  };

  if (!data || !Array.isArray(data)) {
    return null;
  }

  data.forEach(d => {
    const entity = {
      '@type': 'Question',
      name: d.Question,
      acceptedAnswer: { '@type': 'Answer', text: d.Answer },
    };
    JSONSchema.push(entity);
  });
  faqSchema['mainEntity'] = JSONSchema;

  return faqSchema;
}

export {
  generateJSONLDSchema,
  generateJSONLDSchemaArticle,
  generateJSONLDSchemaHowTo,
  generateJSONLDSchemaFAQ,
};
